import React, { useState, useEffect, useCallback } from "react";
import queryString from "query-string";
import GoViralStrategyLanding from "../../components/GoViralStrategyLanding";
import AuthForm from "../../components/AuthForm";
import ResponsiveHeader from "../../components/ResponsiveHeader";
import CommonModalWrapper from "../../components/CommonModalWrapper";
import TargetAudienceForm from "../../components/TargetAudienceForm";
import SettingsForm from "../../components/SettingsForm";
import config from "../../config";
import SessionService from "../../services/Session";
import { displayToastMessage } from "../../utils/Toasts";
import { logIssueNetworkRequestError } from "../../utils/Loggers";
import { useLocation } from "react-router-dom";
import { logAndFormatFormError } from "../../utils/Errors";

const App: React.FC = () => {
  const location = useLocation();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [targetAudienceModalOpen, setTargetAudienceModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [initialAuthView, setInitialAuthView] = useState("login");
  const [emailToken, setEmailToken] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState<any>({
    id: "",
    name: "",
    email: "",
    emailNotifications: false,
  });
  const [selectedPlan, setSelectedPlan] = useState("basic");

  const handleInitialLoad = useCallback(async () => {
    if (!config.inProd) {
      setUserData({
        id: "user-123",
        name: "Demo User",
        email: "example@gvs.com",
        emailNotifications: true,
      });
      return;
    }
    try {
      const sessionService = new SessionService();

      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.token && parsedQuery.view === "activate") {
        displayToastMessage(
          "success",
          "Success. If you need your activation token, check your email."
        );
        setEmailToken(parsedQuery.token);
        setInitialAuthView("activateAccount");
        setAuthModalOpen(true);
        return;
      }
      if (parsedQuery.token && parsedQuery.view === "resetPassword") {
        displayToastMessage(
          "success",
          "Success. If you need your reset token, check your email."
        );
        setEmailToken(parsedQuery.token);
        setInitialAuthView("resetPassword");
        setAuthModalOpen(true);
        return;
      }

      const sessionData = await sessionService.reviewSession();
      if (sessionData.success) {
        setUserData(sessionData.userSessionData);
        setIsAuthenticated(true);
      }
    } catch (error: any) {
      const errorMessage = logAndFormatFormError(
        "Auth.handleInitialLoad",
        error,
        "Generating content strategy"
      );
      console.log("errorMessage:", errorMessage);
    }
  }, [location]);

  // On load...
  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const handleLogin = useCallback(() => {
    setInitialAuthView("login");
    setAuthModalOpen(true);
  }, []);

  const handleSignup = useCallback(
    (planSelected: string) => {
      setSelectedPlan(planSelected);
      setInitialAuthView("signup");
      setAuthModalOpen(true);
    },
    [setInitialAuthView, setAuthModalOpen]
  );

  const handleLogout = useCallback(async () => {
    if (!config.inProd) {
      setIsAuthenticated(false);
      setUserData({
        id: "",
        name: "",
        email: "",
        emailNotifications: false,
      });
      return;
    }
    try {
      const sessionService = new SessionService();
      await sessionService.signOut();
      setIsAuthenticated(false);
      displayToastMessage("success", "You have been logged out successfully");
    } catch (error: any) {
      logIssueNetworkRequestError("Auth.handleLogout", error);
    }
  }, [setIsAuthenticated, setUserData]);

  const handleCreateReport = useCallback(() => {
    setTargetAudienceModalOpen(true);
  }, [setTargetAudienceModalOpen]);

  const handleOpenSettings = useCallback(() => {
    setSettingsModalOpen(true);
  }, [setSettingsModalOpen]);

  const handleSettingsSuccess = useCallback(
    (updatedSettings: any) => {
      if (updatedSettings.email && updatedSettings.email !== userData.email) {
        setUserData((prev: any) => ({
          ...prev,
          email: updatedSettings.email,
        }));
      }
      if (
        updatedSettings.emailNotifications &&
        updatedSettings.emailNotifications !== userData.emailNotifications
      ) {
        setUserData((prev: any) => ({
          ...prev,
          emailNotifications: updatedSettings.emailNotifications,
        }));
      }
    },
    [setUserData, userData.email, userData.emailNotifications]
  );

  // Function to handle auth modal close with successful authentication
  const handleAuthSuccess = (userSessionData: any) => {
    if (!config.inProd) {
      setUserData({
        id: "user-123", // Default ID for demo purposes
        name: "User",
        email: "user@example.com",
      });
      return;
    } else {
      setUserData(userSessionData);
    }
    setAuthModalOpen(false);
    setIsAuthenticated(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <ResponsiveHeader
        isAuthenticated={isAuthenticated}
        userName={userData.name}
        onLogin={handleLogin}
        onSignup={handleSignup}
        onLogout={handleLogout}
        onCreateReport={handleCreateReport}
        onOpenSettings={handleOpenSettings}
      />

      {/* Main content */}
      <main className="flex-grow">
        <GoViralStrategyLanding
          isAuthenticated={isAuthenticated}
          onSignup={handleSignup}
          onSetTargetAudienceModalOpen={setTargetAudienceModalOpen}
        />
      </main>

      {/* Auth Modal */}
      <CommonModalWrapper
        maxWidth="xl"
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      >
        <AuthForm
          emailToken={emailToken}
          selectedPlan={selectedPlan}
          initialView={initialAuthView}
          onAuthSuccess={handleAuthSuccess}
        />
      </CommonModalWrapper>

      {/* Target Audience Modal */}
      <CommonModalWrapper
        maxWidth="4xl"
        isOpen={targetAudienceModalOpen}
        onClose={() => setTargetAudienceModalOpen(false)}
      >
        <TargetAudienceForm userId={userData.id} />
      </CommonModalWrapper>

      {/* Settings Modal */}
      <CommonModalWrapper
        maxWidth="5xl"
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      >
        <SettingsForm
          userId={userData.id}
          userEmail={userData.email}
          emailNotifications={userData.emailNotifications}
          onClose={() => setSettingsModalOpen(false)}
          onSuccess={handleSettingsSuccess}
          onLogout={handleLogout}
        />
      </CommonModalWrapper>
    </div>
  );
};

export default App;
